import { Button, Col, Row, Spinner, Container } from "react-bootstrap";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import Rotate90DegreesCcwIcon from "@mui/icons-material/Rotate90DegreesCcw";
import { useListDiffsQuery } from "../../backend/hooks/interactive/queryListDiffs";
import { useCreateDifferenceMutation } from "../../backend/hooks/interactive/mutationCreateDifference";
import Error from "../abstractComponents/error";
import { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  DataGridPro,
  GridColDef,
  GridRowId,
  GridRowParams,
  GridSelectionModel
} from "@mui/x-data-grid-pro";
import FileIcon from "../abstractComponents/fileIcon";
import CloseIcon from "@mui/icons-material/Close";
import { useImportFilesMutation } from "../../backend/hooks/interactive/mutationImportFiles";
import { InteractiveFileTransferImportSimple } from "./interactiveFileTransferImportSimple";
import { InteractiveFileTransferImportAction } from "./interactiveFileTransferImportAction";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import { formatStringToDateTime } from "../../helpers/stringHelper";
import { Stack } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  compressedFileExtensions,
  excelFormatsExtensions,
  imageExtensions,
  installersExtensions,
  isFileEndsWith,
  moviesExtensions
} from "../../helpers/fileHelper";
import Success from "../abstractComponents/success";
import { Difference, DifferenceLocation } from "../../backend/interactiveTypes";
import { Repo, ValidityStatus } from "../../backend/types";
import UserManualLink from "../abstractComponents/usermanual";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

export type CustomDifference = Difference & {
  repoName: string;
  repoId: string | undefined;
  fileName: string;
};

export function InteractiveFileTransfer(props: { environmentId: string }) {
  const { activityId } = useParams<{ activityId: string }>();
  const history = useHistory();
  const [shouldPollDifferences, setShouldPollDifferences] =
    useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<DifferenceLocation>(
    DifferenceLocation.Ie
  );
  const location = selectedLocation;
  const [selectedRows, setSelectedRows] = useState<CustomDifference[] | null>(
    null
  );
  const [displayRows, setDisplayRows] = useState<CustomDifference[]>([]);
  const [pageSize, setPageSize] = useState(1000);
  const [page, setPage] = useState(0);
  const refetchDiffState = useLocation().type as {
    refetchDiffState: boolean;
  };
  const [searchName, setSearchName] = useState<string>("Search name...");
  const [successMessage, setSuccessMessage] = useState<{
    message: string;
    id: number;
  } | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const searchTimeoutRef = useRef<number | null>(null);
  const pageCache = useRef<any>({});
  const previousSelectedLocation = useRef<DifferenceLocation>(selectedLocation);
  // Reset the cache entirely if the pageSize changes
  useEffect(() => {
    pageCache.current = {};
    setPage(0);
  }, [pageSize]);

  const { activityData } = useContext(PmxActivityDetailsContext);

  const {
    createDifference,
    differenceCreated,
    isLoadingCreateDifference,
    errorCreateDifference
  } = useCreateDifferenceMutation();

  const {
    refetchDiff,
    getListDiffs,
    diffs,
    isLoadingListDiffs,
    errorListDiffs
  } = useListDiffsQuery(shouldPollDifferences);

  const repositories: Repo[] = useMemo(
    () => [
      ...(Array.isArray(activityData?.readOnlyRepositories)
        ? activityData.readOnlyRepositories.filter(
            (repo: Repo | null | undefined): repo is Repo => !!repo
          )
        : []),
      ...(activityData?.mainRepository ? [activityData.mainRepository] : [])
    ],
    [activityData]
  );

  const transformDifferences = (
    differences: Difference[]
  ): CustomDifference[] => {
    return differences.reduce(
      (currentArray: CustomDifference[], file: Difference) => {
        const nameSplit = file.key.split("/");
        const repoName = nameSplit[0];
        nameSplit.shift();
        const fileName = nameSplit.join("/");

        currentArray.push({
          ...file,
          repoName,
          repoId:
            repositories.length > 0
              ? repositories.find(
                  (repoData: Repo) => repoData && repoData.name === repoName
                )?.id
              : undefined,
          fileName
        });

        return currentArray;
      },
      []
    );
  };

  useEffect(() => {
    if (refetchDiffState) {
      window.history.replaceState({ refetchDiffState: false }, "");
      computeNewDifference();
    }
  }, [refetchDiffState]);

  useEffect(() => {
    setShouldPollDifferences(
      !(diffs?.stoppedAt || (!diffs?.diffId && !diffs?.stoppedAt))
    );
    if (diffs?.stoppedAt) {
      const processed = transformDifferences(diffs?.differences || []);
      setDisplayRows(processed);
    }
  }, [diffs?.diffId, diffs?.stoppedAt]);

  useEffect(() => {
    const cacheKey = `${page}-${pageSize}`;
    const selectedLocationChanged =
      previousSelectedLocation.current !== selectedLocation;

    previousSelectedLocation.current = selectedLocation;

    const fetchData = async () => {
      if (!selectedLocationChanged && pageCache.current[cacheKey]) {
        setDisplayRows(pageCache.current[cacheKey]);
        return;
      }

      const { data } = await getListDiffs({
        variables: {
          environmentId: props.environmentId,
          limit: pageSize,
          name: searchName !== "Search name..." ? searchName : undefined,
          location: selectedLocation,
          cursor: nextCursor
        }
      });

      const processed = transformDifferences(
        data?.listDifferences?.differences || []
      );
      if (!selectedLocationChanged) {
        pageCache.current[cacheKey] = processed;
      }
      setDisplayRows(processed);
      if (data?.listDifferences?.nextCursor) {
        setNextCursor(data.listDifferences.nextCursor);
      }
    };

    fetchData();
  }, [page, pageSize, searchName, selectedLocation, nextCursor]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        window.clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSelectMultipleRows = (e: GridSelectionModel) => {
    setSelectedRows((previousSelectedRows) => {
      const currentSelectedRows = previousSelectedRows ?? [];
      const currentSelectedKeys = new Set(
        currentSelectedRows.map((row) => row.key)
      );
      const nextSelectedKeys = new Set(
        e.map((row: GridRowId) => row.toString())
      );

      const retainedRows = currentSelectedRows.filter((row) =>
        nextSelectedKeys.has(row.key)
      );

      const newlySelectedRows = e
        .map((row: GridRowId) => row.toString())
        .filter((rowPath) => !currentSelectedKeys.has(rowPath))
        .map((rowPath) =>
          displayRows.find((item: CustomDifference) => item.key === rowPath)
        )
        .filter((item): item is CustomDifference => item !== undefined);

      return [...retainedRows, ...newlySelectedRows];
    });
  };

  const generateCompareUrl = (params: any): string => {
    return (
      `/activity/${activityId}/interactive/` +
      props.environmentId +
      "/compare?" +
      encodeURIComponent(`repoId=${params.row.repoId}`) +
      encodeURIComponent(`&repoName=${params.row.repoName}`) +
      encodeURIComponent(`&path=${params.row.fileName}`) +
      encodeURIComponent(
        `&phil=${selectedLocation === DifferenceLocation.Phil ? true : false}`
      ) +
      encodeURIComponent(`&conflict=${params.row.conflict}`)
    );
  };

  const isFileHidden = (row: any) => {
    const excludedExtensions = [
      imageExtensions,
      compressedFileExtensions,
      moviesExtensions,
      installersExtensions,
      excelFormatsExtensions
    ].flat();
    excludedExtensions.push(".pdf");
    return (
      (row.type?.toLowerCase() === "modified" &&
        !isFileEndsWith(row.fileName, excludedExtensions)) ||
      (row.conflict && !isFileEndsWith(row.fileName, excludedExtensions))
    );
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 4,
      renderCell: (params: any) => (
        <>
          <FileIcon
            fileName={params.row.key}
            overallValidityStatus={ValidityStatus.NotSet}
            fontSize={"large"}
            className={"description_icon"}
          />{" "}
          {params.row.key}
        </>
      ),
      valueGetter: (params: any) => params.row.key
    },
    {
      field: "Compare",
      headerName: "Compare",
      flex: 1,
      renderCell: (params: any) =>
        isFileHidden(params.row) && (
          <Button
            onClick={() => history.push(generateCompareUrl(params))}
            data-testid="compare-button"
          >
            Compare
          </Button>
        )
    },
    {
      field: "state",
      headerName: "State",
      flex: 1,
      renderCell: (params: any) => (
        <span
          className={
            params.row.type.toLowerCase() === "added"
              ? "interactive_item_state_created"
              : params.row.type.toLowerCase() === "deleted"
                ? "interactive_item_state_deleted"
                : "interactive_item_state_modified"
          }
        >
          {params.row.type}
          {params.row.conflict && (
            <>
              {" "}
              -{" "}
              <span className="interactive_item_state_deleted isConflict">
                Conflict
              </span>
            </>
          )}
        </span>
      )
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated Date",
      flex: 2,
      valueGetter: (params: any) =>
        formatStringToDateTime(params.row.modifiedAt * 1000)
    }
  ];

  const { importFiles, idImportFiles, isLoadingImportFiles, errorImportFiles } =
    useImportFilesMutation();

  const hasConflictInImport = () => {
    if (selectedRows?.some((row) => row.conflict)) {
      return window.confirm(
        "Are you sure you want to overwrite the files already in Interactive?"
      );
    } else {
      return true;
    }
  };

  const triggerImport = () => {
    if (hasConflictInImport()) {
      importFiles({
        variables: {
          environmentId: props.environmentId,
          importPaths: selectedRows?.map((row) => ({
            repoName: row.repoName,
            path: row.fileName
          }))
        }
      });
    }
  };

  const computeNewDifference = () => {
    createDifference({
      variables: {
        environmentId: props.environmentId
      }
    }).then(() => {
      setShouldPollDifferences(true);
      refetchDiff();
      resetState();
    });
  };

  const RefreshButton = () => {
    return (
      <Button
        id="interactive_file_transfer"
        className="button-secondary float-end"
        variant="outline-primary"
        onClick={computeNewDifference}
      >
        <RefreshIcon /> Reload Diff
      </Button>
    );
  };

  const loadingDifferenceText = () => {
    if (isLoadingListDiffs && !shouldPollDifferences) {
      return "Loading Differences...";
    } else if (isLoadingImportFiles) {
      return "Importing Files...";
    } else if (
      isLoadingCreateDifference ||
      diffs.stoppedAt === null ||
      shouldPollDifferences
    ) {
      return "Computing Differences...";
    }
  };

  // Keep the grid believing there is always an extra page waiting
  const mockRowCount: number = nextCursor
    ? (page + 2) * pageSize
    : diffs.differences.length;

  const abortControllerRef = useRef<AbortController | null>(null);
  const searchListDiffs = (name: string) => {
    setSearchName(name);

    setPage(0); // Reset to the first page when a new search is initiated
    setPageSize(1000); // Reset to the default page size when a new search is initiated
    setNextCursor(null); // Reset the next cursor when a new search is initiated

    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = window.setTimeout(() => {
      // Abort the previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const fetchData = async () => {
        await getListDiffs({
          variables: {
            environmentId: props.environmentId,
            limit: 1000,
            name: name,
            location: selectedLocation
          },
          context: {
            fetchOptions: {
              signal: abortController.signal
            },
            clientName: "interactive"
          }
        })
          .then((response) => {
            response.data?.listDifferences?.differences &&
              setDisplayRows(
                transformDifferences(response.data.listDifferences.differences)
              );
            setNextCursor(response.data?.listDifferences?.nextCursor || null);
          })
          .finally(() => {
            // Only nullify if this was the last controller created
            if (abortControllerRef.current === abortController) {
              abortControllerRef.current = null;
            }
          });
      };
      fetchData();
    }, 1000);
  };

  const searchLoading =
    (isLoadingListDiffs && searchName !== "Search name...") ||
    (isLoadingListDiffs && displayRows.length === 0) ||
    (isLoadingListDiffs && nextCursor !== null);

  const swapSourceDestination = () => {
    const nextLocation =
      selectedLocation === DifferenceLocation.Ie
        ? DifferenceLocation.Phil
        : DifferenceLocation.Ie;
    setSelectedLocation(nextLocation);
    resetState();
  };

  const resetState = () => {
    setSearchName("Search name...");
    setSelectedRows([]);
    setPage(0);
    setPageSize(1000);
    setNextCursor(null);
  };

  return (
    <div className="interactive_file_transfer">
      {isLoadingImportFiles ||
      (isLoadingListDiffs && searchName === "Search name..." && !nextCursor) ||
      isLoadingCreateDifference ||
      shouldPollDifferences ? (
        <Row className="loading_row">
          <Col className="text-center">
            <Spinner animation="border" className="spinner_color" />
            <br />
            {loadingDifferenceText()}
          </Col>
        </Row>
      ) : (
        <>
          <Row className="interactive_transfer_source_graph">
            <Col xs={1}>
              <div className="interactive_transfer_source">Source</div>
              {selectedLocation === DifferenceLocation.Ie
                ? "Interactive"
                : "PHIL"}
            </Col>
            <Col xs={1} className="text-center">
              <DoubleArrowIcon className="interactive_direction_display" />
            </Col>
            <Col xs={1}>
              <div className="interactive_transfer_source">Destination</div>
              {selectedLocation === DifferenceLocation.Ie
                ? "PHIL"
                : "Interactive"}
            </Col>
            <Col xs={1} className="interactive_transfer_source_button">
              <Button
                variant="outline-dark"
                onClick={() => {
                  swapSourceDestination();
                }}
              >
                <CompareArrowsIcon /> Swap
              </Button>
            </Col>
            {diffs.stoppedAt ? (
              <Col xs={2}>
                <div className="interactive_transfer_source">
                  Last Calculated Difference
                </div>
                {formatStringToDateTime(diffs?.stoppedAt * 1000)}
              </Col>
            ) : (
              <Col xs={2}>
                <div className="interactive_transfer_source">
                  Difference Calculation Started At
                </div>
                {formatStringToDateTime(diffs?.startedAt * 1000)}
              </Col>
            )}
            <Col xs={4}>
              {selectedRows && selectedRows.length > 0 && (
                <div className="d-flex justify-content-end">
                  {selectedLocation === DifferenceLocation.Ie ? (
                    <>
                      <InteractiveFileTransferImportSimple
                        environmentId={props.environmentId}
                        selectedRows={selectedRows}
                        setSuccessMessage={setSuccessMessage}
                      />
                      <InteractiveFileTransferImportAction
                        environmentId={props.environmentId}
                        selectedRows={selectedRows}
                        setSuccessMessage={setSuccessMessage}
                      />
                    </>
                  ) : (
                    <Button
                      id="proced_with_import"
                      className="button-secondary"
                      variant="primary"
                      onClick={triggerImport}
                    >
                      <ArrowForwardIcon /> Import Selected Files
                    </Button>
                  )}
                </div>
              )}
            </Col>
            <Col xs={2}>
              <UserManualLink
                url="/usermanual/datatransfer"
                testId="um-actions"
              />
              <RefreshButton />
            </Col>
          </Row>
          <Container
            fluid
            className="browse-pmx-activity-container d-flex flex-column"
          >
            <Row className="mt-4 flex-grow-1 overflow-hidden interactive_transfer_source_display">
              <Col xs={3} className="d-flex flex-column h-100 border-end">
                <h5 className="text-center flex-shrink-0 mb-3">
                  Selected Files
                </h5>
                <div className="flex-grow-1 overflow-y-auto overflow-x-hidden w-100 text-break">
                  {selectedRows?.map((selectedItem: any) => {
                    const displayName = `${selectedItem?.repoName}/${selectedItem?.key}`;
                    return (
                      <div
                        key={displayName}
                        className="mb-2 pb-2 border-bottom text-break"
                      >
                        <CloseIcon
                          role="button"
                          aria-label="close"
                          className="interactive_item_state_deleted pe-auto"
                          onClick={() => {
                            const updatedSelectedRows = selectedRows.filter(
                              (row) =>
                                `${row.repoName}/${row.key}` !== displayName
                            );
                            setSelectedRows(updatedSelectedRows);
                          }}
                        />
                        <FileIcon
                          fileName={displayName}
                          overallValidityStatus={ValidityStatus.NotSet}
                          fontSize={"small"}
                          absolutePath={displayName}
                        />{" "}
                        {selectedItem?.type &&
                          selectedItem?.type.toLowerCase() === "added" && (
                            <span
                              title={
                                selectedItem.conflict
                                  ? "File Created with conflict"
                                  : "File Created"
                              }
                            >
                              <AddCircleOutlineIcon className="interactive_item_state_created float-end" />
                              {selectedItem.conflict && (
                                <PriorityHighIcon className="interactive_item_state_deleted float-end is_conflict" />
                              )}
                            </span>
                          )}
                        {selectedItem?.type &&
                          selectedItem?.type.toLowerCase() === "deleted" && (
                            <span
                              title={
                                selectedItem.conflict
                                  ? "File Deleted with conflict"
                                  : "File Deleted"
                              }
                            >
                              <CancelPresentationIcon className="interactive_item_state_deleted float-end" />
                              {selectedItem.conflict && (
                                <PriorityHighIcon className="interactive_item_state_deleted float-end is_conflict" />
                              )}
                            </span>
                          )}
                        {selectedItem?.type?.toLowerCase() === "modified" && (
                          <span
                            title={
                              selectedItem?.conflict
                                ? "File was Modified with conflict"
                                : "File Modified"
                            }
                          >
                            <Rotate90DegreesCcwIcon className="interactive_item_state_modified float-end" />
                            {selectedItem?.conflict && (
                              <PriorityHighIcon className="interactive_item_state_deleted float-end is_conflict" />
                            )}
                          </span>
                        )}
                        {displayName}
                      </div>
                    );
                  })}
                </div>
              </Col>

              <Col xs={9} className="d-flex flex-column h-100 overflow-hidden">
                <div className="flex-shrink-0 mb-2">
                  <TextField
                    className="w-100"
                    variant="standard"
                    placeholder="Search differences..."
                    onChange={(e) => searchListDiffs(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                    error={diffs.differences.length >= 1000}
                    helperText={
                      diffs.differences.length >= 1000 &&
                      "There are too many differences to load, please refine your search."
                    }
                  />
                </div>

                <div className="flex-grow-1 overflow-hidden">
                  <DataGridPro
                    paginationMode="server"
                    rowCount={mockRowCount}
                    loading={searchLoading}
                    rows={displayRows}
                    columns={columns}
                    getRowId={(row) => row.key}
                    rowHeight={50}
                    autoHeight={false} // Important: Disable autoHeight to respect parent container
                    disableSelectionOnClick
                    keepNonExistentRowsSelected
                    selectionModel={selectedRows?.map((row) => row.key) || []}
                    onSelectionModelChange={(e: GridSelectionModel) =>
                      handleSelectMultipleRows(e)
                    }
                    checkboxSelection
                    isRowSelectable={(params: GridRowParams) =>
                      (selectedLocation === DifferenceLocation.Ie &&
                        activityData?.mainRepository?.name &&
                        params.row.key.startsWith(
                          activityData.mainRepository.name
                        )) ||
                      selectedLocation === DifferenceLocation.Phil
                    }
                    initialState={{
                      sorting: { sortModel: [{ field: "name", sort: "asc" }] }
                    }}
                    components={{
                      NoRowsOverlay: () => (
                        <Stack
                          height="100%"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {displayRows.length === 0 &&
                            `No Differences in ${
                              selectedLocation === DifferenceLocation.Ie
                                ? "Interactive"
                                : "PhIL"
                            }.`}
                        </Stack>
                      )
                    }}
                    componentsProps={{
                      pagination: {
                        SelectProps: { disabled: isLoadingListDiffs },
                        backIconButtonProps: { disabled: isLoadingListDiffs },
                        nextIconButtonProps: { disabled: isLoadingListDiffs }
                      }
                    }}
                    pagination
                    pageSize={pageSize}
                    page={page}
                    onPageChange={(newPage) => setPage(newPage)}
                    onPageSizeChange={(newPageSize) => {
                      setPageSize(newPageSize);
                      setPage(0);
                    }}
                    rowsPerPageOptions={[50, 100, 500, 1000]}
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </>
      )}

      {(errorListDiffs || errorImportFiles || errorCreateDifference) && (
        <Error
          error={errorListDiffs || errorImportFiles || errorCreateDifference}
        />
      )}
      {[
        differenceCreated &&
          "Difference Calculation Request Created Successfully",
        successMessage?.message,
        idImportFiles &&
          "Importing files, please check the notification for progress."
      ]
        .filter((msg): msg is string => !!msg)
        .map((message) => (
          <Success key={successMessage?.id || message} message={message} />
        ))}
    </div>
  );
}
